import { EMPTY_CHARACTER, NetworkingError } from "@pulse/shared-components";
import {
  FileUploadError,
  getFileSHA,
  uploadFileToS3,
  validateSpocRespondentUnmappingCSVFile,
} from "../../utils/FileUploadUtils";
import { Instance, flow, getRoot, types } from "mobx-state-tree";
import { FileAttributes } from "@pulse/pulse-rpcs/lib/types/fileAttributes";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { insertDocumentRecord } from "../../utils/FileDownloadUtils";
import { GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC } from "@pulse/pulse-rpcs/lib/spoc-management/getPresignedDocumentUploadUrlForSpocRespondentUnmappingRPC";
import {
  useGetPresignedUploadURLForSpocRespondentUnmappingRPCClientImpl,
  useValidateCSVAndUnmapRespondentsFromSpocsRPCClientImpl,
} from "../rpcs/RPC";
import { RootStore } from "../../root/store/RootStore";
import { CSV_FILE_TYPE, SHA_256_LENGTH } from "@pulse/shared-components";
import { MIN_FILE_NAME } from "@pulse/shared-components";
import { MAX_FILE_NAME } from "@pulse/shared-components";
import { MAX_RESPONDENT_SPOC_MAPPING_FILE_SIZE_IN_BYTES } from "@pulse/shared-components";
import { ValidateCSVAndUnmapRespondentsFromSpocsRPC } from "@pulse/pulse-rpcs/lib/spoc-management/validateCsvAndUnmapRespondentsFromSpocsRPC";

export enum ValidateCSVAndUnmapSPoCsErrors {
  InvalidFile = "INVALID_FILE",
  UnknownFile = "UNKNOWN_FILE",
  SpocAssignmentInProgress = "SPOC_ASSIGNMENT_IN_PROGRESS",
  ProjectArchived = "PROJECT_ARCHIVED",
  InvalidProjectId = "INVALID_PROJECT_ID",
}

export enum GetPresignedDownloadURLForSPoCssWithFiltersErrors {
  DeletedColumnFilter = "DELETED_COLUMN_FILTER",
}

export enum DownloadSPoCsCSVDialogState {
  DownloadConfirmation = "DOWNLOAD_CONFIRMATION",
  SuccessState = "SUCCESS_STATE",
  ErrorState = "ERROR_STATE",
}

export enum SPoCDownloadFiltersEnum {
  Name = "Name",
  EmailAddress = "Email Address",
  AddedOn = "Added On",
  ExpiresOn = "Expires On",
}

export const SPoCDownloadAndUnmapStore = types
  .model("SPoCDownloadAndUnmapStore", {
    rpcErrors: types.maybeNull(
      types.union(
        types.enumeration(
          Object.values(GetPresignedDownloadURLForSPoCssWithFiltersErrors),
        ),
        types.enumeration(Object.values(ValidateCSVAndUnmapSPoCsErrors)),
        types.enumeration(Object.values(NetworkingError)),
      ),
    ),
    downloadSPoCsCSVDialogState: types.optional(
      types.enumeration(Object.values(DownloadSPoCsCSVDialogState)),
      DownloadSPoCsCSVDialogState.DownloadConfirmation,
    ),
    isDownloadSPoCsCSVDialogVisible: types.optional(types.boolean, false),
    isUnmapMultipleSPoCsDialogVisible: types.optional(types.boolean, false),
    isUnmapMultipleSPoCsUploadDialogVisible: types.optional(
      types.boolean,
      false,
    ),
    isUnmapMultipleSPoCsValidationDialogVisible: types.optional(
      types.boolean,
      false,
    ),
    isRPCLoading: types.optional(types.boolean, false),
    fileName: types.optional(types.string, EMPTY_CHARACTER),
    validationError: types.maybeNull(
      types.enumeration(Object.values(FileUploadError)),
    ),
    invalidFileDetails: types.maybeNull(types.string),
    documentId: types.maybeNull(types.string),
    isFileValidated: types.optional(types.boolean, false),
    isFileInvalid: types.optional(types.boolean, false),
    hasFileUploaded: types.optional(types.boolean, false),
  })
  .actions((store) => ({
    resetSPoCsCSVDialog: (): void => {
      store.downloadSPoCsCSVDialogState =
        DownloadSPoCsCSVDialogState.DownloadConfirmation;
      store.rpcErrors = null;
    },
    setIsDownloadSPoCsCSVDialogVisible: (isDialogVisible: boolean): void => {
      store.isDownloadSPoCsCSVDialogVisible = isDialogVisible;
    },
    resetUnmapMultipleSPoCsDialog: (): void => {
      store.rpcErrors = null;
      store.validationError = null;
    },
    setIsUnmapMultipleSPoCsUploadDialogVisible: (
      isDialogVisible: boolean,
    ): void => {
      store.isUnmapMultipleSPoCsUploadDialogVisible = isDialogVisible;
    },
    setIsUnmapMultipleSPoCsValidationDialogVisible: (
      isDialogVisible: boolean,
    ): void => {
      store.isUnmapMultipleSPoCsValidationDialogVisible = isDialogVisible;
    },
    setIsFileValidated: (isValidated: boolean): void => {
      store.isFileValidated = isValidated;
    },
    setIsFileInvalid: (isInvalid: boolean): void => {
      store.isFileInvalid = isInvalid;
    },
    setHasFileUploaded: (hasUploaded: boolean): void => {
      store.hasFileUploaded = hasUploaded;
    },
    resetUnmapCSVStates: (): void => {
      store.fileName = EMPTY_CHARACTER;
      store.validationError = null;
      store.invalidFileDetails = null;
      store.documentId = null;
      store.isFileValidated = false;
      store.isFileInvalid = false;
      store.hasFileUploaded = false;
    },
    downloadUnmapTemplate: flow(function* () {
      // Placeholder for download template functionality
      console.log("Download unmap template");
    }),
    setIsRPCLoading: (isLoading: boolean): void => {
      store.isRPCLoading = isLoading;
    },
    setFileName: (fileName: string): void => {
      store.fileName = fileName;
    },
    resetValidationError: (): void => {
      store.validationError = null;
    },
    resetRPCError: (): void => {
      store.rpcErrors = null;
    },
    getFileAttribute: flow(function* (file: File) {
      if (!CSV_FILE_TYPE.includes(file.type)) {
        store.validationError = FileUploadError.InvalidFileFormat;
        return;
      }
      const sha256 = yield getFileSHA(file);

      try {
        yield validateSpocRespondentUnmappingCSVFile(file)
          .then((csv) => csv)
          .catch((error) => {
            throw new Error(error);
          });
      } catch (error) {
        if (error instanceof Error) {
          switch (error.message) {
            case FileUploadError.BaseColumnsNotFound: {
              store.validationError = FileUploadError.BaseColumnsNotFound;
              return;
            }
            case FileUploadError.MaxColumnsExceeded: {
              store.validationError = FileUploadError.MaxColumnsExceeded;
              return;
            }
            case FileUploadError.MaxRowsExceeded: {
              store.validationError = FileUploadError.MaxRowsExceeded;
              return;
            }
          }
        }
      }

      try {
        if (sha256.length !== SHA_256_LENGTH) {
          store.validationError = FileUploadError.InvalidFileSHA;
          return;
        } else if (
          file.name.length < MIN_FILE_NAME ||
          file.name.length > MAX_FILE_NAME
        ) {
          store.validationError = FileUploadError.InvalidFileName;
          return;
        } else if (file.size > MAX_RESPONDENT_SPOC_MAPPING_FILE_SIZE_IN_BYTES) {
          store.validationError = FileUploadError.MaxFileSizeReached;
          return;
        }

        const fileAttribute = new FileAttributes(sha256, file.name, file.size);
        return fileAttribute;
      } catch (error) {
        store.validationError = FileUploadError.InternalError;
      }
    }),
  }))
  .actions((store) => ({
    uploadCSVFile: flow(function* (file: File, projectId: string) {
      store.isRPCLoading = true;
      store.resetValidationError();
      store.resetUnmapMultipleSPoCsDialog();
      store.resetRPCError();
      try {
        const apiClient = getAPIClient(store);
        const fileAttribute: FileAttributes | undefined =
          yield store.getFileAttribute(file);
        console.log("fileAttribute", fileAttribute);
        if (fileAttribute === undefined) {
          return;
        }
        const request =
          new GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.Request(
            new LeoUUID(projectId),
            fileAttribute,
          );
        const {
          response,
          error,
        }: {
          response?: GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.Response;
          error?: GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC.Errors.Errors;
        } =
          yield useGetPresignedUploadURLForSpocRespondentUnmappingRPCClientImpl(
            apiClient,
          ).execute(request);
        if (response) {
          try {
            yield uploadFileToS3(response.documentURL, file);
            store.documentId = yield insertDocumentRecord(
              fileAttribute,
              apiClient,
            );
            store.hasFileUploaded = true;
            store.isFileValidated = true;
            store.isFileInvalid = false;
          } catch (e) {
            store.validationError = FileUploadError.InternalError;
            store.isFileValidated = false;
            store.isFileInvalid = true;
          }
        } else if (error) {
          switch (error.code) {
            case GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC
              .RPCError.SpocAssignmentInProgress:
              store.rpcErrors =
                ValidateCSVAndUnmapSPoCsErrors.SpocAssignmentInProgress;
              break;
            case GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC
              .RPCError.InvalidProjectId:
              store.rpcErrors = ValidateCSVAndUnmapSPoCsErrors.InvalidProjectId;
              break;
            case GetPresignedDocumentUploadURLForSpocRespondentUnmappingRPC
              .RPCError.ProjectAlreadyArchived:
              store.rpcErrors = ValidateCSVAndUnmapSPoCsErrors.ProjectArchived;
              break;
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occured: ${e}`);
        }
      } finally {
        store.isRPCLoading = false;
      }
    }),
    validateCSVAndUnmapSPoCs: flow(function* (projectId: string) {
      store.isRPCLoading = true;
      store.resetValidationError();
      store.resetUnmapMultipleSPoCsDialog();
      store.resetRPCError();
      try {
        const apiClient = getAPIClient(store);
        const request = new ValidateCSVAndUnmapRespondentsFromSpocsRPC.Request(
          new LeoUUID(projectId),
          new LeoUUID(store.documentId),
        );
        const {
          error,
        }: {
          error?: ValidateCSVAndUnmapRespondentsFromSpocsRPC.Errors.Errors;
        } =
          yield useValidateCSVAndUnmapRespondentsFromSpocsRPCClientImpl(
            apiClient,
          ).execute(request);
        if (error) {
          switch (error.code) {
            case ValidateCSVAndUnmapRespondentsFromSpocsRPC.RPCError
              .SpocAssignmentInProgress:
              store.rpcErrors =
                ValidateCSVAndUnmapSPoCsErrors.SpocAssignmentInProgress;
              break;
            case ValidateCSVAndUnmapRespondentsFromSpocsRPC.RPCError
              .InvalidProjectId:
              store.rpcErrors = ValidateCSVAndUnmapSPoCsErrors.InvalidProjectId;
              break;
            case ValidateCSVAndUnmapRespondentsFromSpocsRPC.RPCError
              .ProjectAlreadyArchived:
              store.rpcErrors = ValidateCSVAndUnmapSPoCsErrors.ProjectArchived;
              break;
            case ValidateCSVAndUnmapRespondentsFromSpocsRPC.RPCError
              .InvalidFile:
              store.rpcErrors = ValidateCSVAndUnmapSPoCsErrors.InvalidFile;
              if (
                error instanceof
                ValidateCSVAndUnmapRespondentsFromSpocsRPC.Errors.InvalidFile
              ) {
                store.invalidFileDetails = error.invalidFileDetails;
                store.isFileInvalid = true;
                store.isUnmapMultipleSPoCsValidationDialogVisible = false;
                store.isUnmapMultipleSPoCsUploadDialogVisible = true;
              }
              break;
            case ValidateCSVAndUnmapRespondentsFromSpocsRPC.RPCError
              .UnknownFile:
              store.rpcErrors = ValidateCSVAndUnmapSPoCsErrors.UnknownFile;
              break;
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          store.validationError = FileUploadError.InternalError;
        }
      } finally {
        store.isRPCLoading = false;
      }
    }),
  }))
  .views((store) => ({
    get isDownloadSPoCsCSVPrimaryButtonVisible(): boolean {
      return store.isDownloadSPoCsCSVDialogVisible;
    },
    get doesStoreContainErrors(): boolean {
      return store.validationError !== null || store.rpcErrors !== null;
    },
    get doesStoreContainValidationError(): boolean {
      return (
        store.validationError !== null || store.invalidFileDetails !== null
      );
    },
  }));

export const createSPoCDownloadAndUnmapStore = (): Instance<
  typeof SPoCDownloadAndUnmapStore
> => {
  return SPoCDownloadAndUnmapStore.create();
};
